import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { LandingProductCurrencyEnum, NewsletterRecurringIntervalEnum } from '@dev/ee-api/ee-api-models';
import { BasicInput, SimplePopover, TextField, Typography, Alert } from '@dev/ee-components';
import { ModifyPaidNewsletter, SetPaidNewsletterErrors, getPaidNewsletterCreatorStore } from '@/store/paidNewsletterCreator/actions';
import { StepContentSeparator } from '@/components/PaidNewsletterCreator/components/StepContentSeparator';
import { useGetProductCurrencySettings } from '@/components/PaidNewsletterCreator/hooks/useGetProductCurrencySettings';
import { validateProductPriceEditValue } from '@/components/PaidNewsletterCreator/tools/validateProductPriceEditValue';
import { PaidNewsletterCreatorStatus, PaidNewsletterErrorNames } from '@/store/paidNewsletterCreator/types';
import { PAID_NEWSLETTER_NAME_MAX_LENGTH } from '@/components/PaidNewsletterCreator/constants';
import { Step1EditContentWrapper } from './styles';
import { getPaidNewsletterErrors } from '../../tools/getPaidNewsletterErrors';
import { getProductMinPrice } from '../../tools/getProductMinPrice';

export const Step1EditContent = (): JSX.Element => {
    const inputRef = useRef<HTMLInputElement>();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getProductCurrencySettings, formatProductPrice } = useGetProductCurrencySettings();
    const { errors, status, paidNewsletterPayload, paidNewsletterObject } = useSelector(getPaidNewsletterCreatorStore);
    
    const { 
        Name : nameValue = '', 
        Currency: productCurrencyValue,
        ProductPrice: productPriceValue,
        RecurringInterval: productRecurringIntervalValue
    } = { ...paidNewsletterObject, ...paidNewsletterPayload };

    const [priceInputValue, setPriceInputValue] = useState(productPriceValue?.toFixed(2) ?? '');

    const onNameChange = (e): void => {
        dispatch(ModifyPaidNewsletter({ Name: e.target.value?.slice(0,PAID_NEWSLETTER_NAME_MAX_LENGTH) }));
        if (!errors) {
            return;
        }
        dispatch(SetPaidNewsletterErrors());
    };
    
    const onProductPriceChange = (e): void => {
        const value = e.target.value;
        if (!validateProductPriceEditValue(value)) {
            return;
        }
        setPriceInputValue(value);
        dispatch(ModifyPaidNewsletter({ ProductPrice: value ? parseFloat(value) : 0 }));
        if (!errors) {
            return;
        }
        dispatch(SetPaidNewsletterErrors());
    };

    const onProductPriceBlur = (): void => {
        setTimeout(() => {
            const fieldErrors = getPaidNewsletterErrors({ ProductPrice: productPriceValue, Currency: productCurrencyValue }, [PaidNewsletterErrorNames.INVALID_PRODUCT_PRICE]);
            if (fieldErrors[PaidNewsletterErrorNames.INVALID_PRODUCT_PRICE]) {
                dispatch(SetPaidNewsletterErrors(fieldErrors));
            }
            const formattedValue = productPriceValue?.toFixed(2);
            if (formattedValue === priceInputValue) {
                return;
            }
            setPriceInputValue(formattedValue);
        },0);
    };

    const onCurrencyChange = (e, obj): void => {
        dispatch(ModifyPaidNewsletter({ Currency: obj.value }));
        if (!errors) {
            return;
        }
        dispatch(SetPaidNewsletterErrors());
    };
    
    const onRecurringIntervalChange = (e, obj): void => {
        dispatch(ModifyPaidNewsletter({ RecurringInterval: obj.value }));
        if (!errors) {
            return;
        }
        dispatch(SetPaidNewsletterErrors());
    };

    useEffect((): void => {
        if (paidNewsletterObject?.Name) {
            return;
        }
        inputRef.current?.focus();
    },[]);

    const currencyOptions = Object.keys(LandingProductCurrencyEnum).map(key => (
        { value: LandingProductCurrencyEnum[key], label: getProductCurrencySettings(LandingProductCurrencyEnum[key])?.label }
    ));

    const recurringIntervalOptions = Object.keys(NewsletterRecurringIntervalEnum).map(key => (
        { value: NewsletterRecurringIntervalEnum[key], label: t(`${NewsletterRecurringIntervalEnum[key]}`) }
    ));

    return (
        <Step1EditContentWrapper>
            <Typography variant="body1">{t('paidnewslettername_description')}</Typography>
            <div>
                <BasicInput
                    inputRef={inputRef}
                    id="paidnewslettercreator-step1-name"
                    label={t('paidnewslettername')}
                    type="input"
                    value={nameValue}
                    fullWidth
                    onChange={onNameChange}
                    autoComplete="off"
                    error={!!!!errors?.[PaidNewsletterErrorNames.DUPLICATED_NAME]}
                    errorMessage={t('paidnewsletternamealreadyinuse')}
                    disabled={status === PaidNewsletterCreatorStatus.SAVING}
                />
            </div>
            <StepContentSeparator />
            <Typography variant="body1">{t('paidnewsletterprice_description')}</Typography>
            {paidNewsletterObject?.Name && <Alert severity="info" body={t('paidnewsletterpricechange_warning')}/>}
            <Grid container wrap="wrap" spacing={3}>
                <Grid item xs={12} sm={4}>
                    <SimplePopover
                        label={t('currency')}
                        type="input"
                        onItemClick={onCurrencyChange}
                        options={currencyOptions}
                        value={productCurrencyValue}
                        defaultValue={LandingProductCurrencyEnum.USD}
                        disableSentenceCase
                        fullWidth
                        boldSelected
                        disabled={status === PaidNewsletterCreatorStatus.SAVING}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <div>
                        <TextField
                            id="paidnewslettercreator-step1-price"
                            name="paidnewslettercreator-step1-price"
                            label={t('productprice')}
                            prefix={getProductCurrencySettings(productCurrencyValue)?.prefix}
                            filterPosition="start"
                            value={priceInputValue}
                            onChange={onProductPriceChange}
                            onBlur={onProductPriceBlur}
                            placeholder="5.00"
                            disabled={status === PaidNewsletterCreatorStatus.SAVING}
                            error={!!errors?.[PaidNewsletterErrorNames.INVALID_PRODUCT_PRICE]}
                            errorMessage={t('pleaseentervalidproductprice', { price: formatProductPrice(productCurrencyValue, getProductMinPrice(productCurrencyValue))})}
                        />
                    </div>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <SimplePopover
                        label={t('frequency')}
                        type="input"
                        onItemClick={onRecurringIntervalChange}
                        options={recurringIntervalOptions}
                        value={productRecurringIntervalValue}
                        defaultValue={NewsletterRecurringIntervalEnum.Monthly}
                        disableSentenceCase
                        fullWidth
                        boldSelected
                        disabled={status === PaidNewsletterCreatorStatus.SAVING}
                    />
                </Grid>
            </Grid>
        </Step1EditContentWrapper>
    );
};
